import { useEffect, useRef, useState } from 'react'

import { countRoll } from '../../lib/motion'

/**
 * 13b's TAIL — the done-log count rolls up one digit.
 *
 * "The count roll is the reward. No burst." (motion-brief §3.) This is the
 * entire celebration budget for finishing something in LifeOS: a number moves up
 * by one, in a window one line tall, and that is all. If a future edit adds a
 * ring, a streak or a flourish beside it, that edit is the bug (README §7,
 * "Deliberately absent").
 *
 * IT ROLLS UP ONLY. A count going DOWN is an un-complete or a correction, not an
 * achievement, and rolling it in the same direction would say otherwise — so a
 * decrease swaps the number with no motion at all. Same rule as 13c: what does
 * not move is information.
 */
export function CountRoll({
  value,
  suffix,
  className,
  /** The height of one line box — the exact distance the track travels. */
  lineHeight = 13,
}: {
  value: number
  suffix?: string
  className?: string
  lineHeight?: number
}) {
  const [shown, setShown] = useState(value)
  /** The digit leaving, or null when nothing is rolling. */
  const [leaving, setLeaving] = useState<number | null>(null)
  const track = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (value === shown) return
    setLeaving(value > shown ? shown : null)
    setShown(value)
  }, [value, shown])

  useEffect(() => {
    if (leaving === null) return
    const run = countRoll(track.current, lineHeight)
    let abandoned = false
    void run.finished.then(() => {
      if (!abandoned) setLeaving(null)
    })
    return () => {
      abandoned = true
      run.cancel()
    }
  }, [leaving, lineHeight])

  const text = (n: number) => (suffix ? `${n} ${suffix}` : String(n))

  return (
    <span className={className}>
      <span
        className="inline-block overflow-hidden align-bottom"
        style={{ height: lineHeight }}
        // The window clips; the value inside it is what a screen reader should hear, once,
        // without the departing digit doubling it.
        aria-label={text(shown)}
      >
        {/* Re-keyed on every roll so the new track starts at translateY(0) rather than
            inheriting the `fill: forwards` transform the last one ended on. */}
        <span
          key={`${leaving}-${shown}`}
          ref={track}
          className="block"
          style={{ lineHeight: `${lineHeight}px` }}
          aria-hidden
        >
          {leaving !== null && <span className="block">{text(leaving)}</span>}
          <span className="block">{text(shown)}</span>
        </span>
      </span>
    </span>
  )
}
